import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, MapPinned, Navigation, ArrowLeft, Clock } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { fetchLocationShareDetail, formatApiError } from "@/lib/api";
import { ReceivedRow, goTo, GONE, fmt, primary } from "@/components/maps/LocationShare";

const left = (iso) => Math.max(0, Date.parse(iso) - Date.now());
const leftText = (ms) => {
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return h >= 1 ? `${h} ghante${m ? ` ${m} min` : ""} aur valid` : `${Math.max(1, m)} min aur valid`;
};

// Live countdown to expires_at; calls onEnd once time runs out.
function Countdown({ iso, onEnd }) {
  const [ms, setMs] = useState(() => left(iso));
  useEffect(() => {
    const t = setInterval(() => setMs(left(iso)), 30000);
    return () => clearInterval(t);
  }, [iso]);
  useEffect(() => { if (ms <= 0) onEnd(); }, [ms]); // eslint-disable-line react-hooks/exhaustive-deps
  if (ms <= 0) return null;
  return <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-800 px-3 py-1 text-xs font-semibold mb-3" data-testid="location-share-countdown"><Clock className="w-3.5 h-3.5" /> {leftText(ms)}</div>;
}

// Bell-alert destination: one share, shown only to its recipient (server-checked); expired/revoked -> clear message.
export default function LocationShareDetail() {
  const { id } = useParams();
  const { user, authChecking } = useApp();
  const [s, setS] = useState(undefined);
  const [err, setErr] = useState("");
  const [opening, setOpening] = useState(false);
  useEffect(() => {
    if (authChecking) return;
    if (!user) { setErr("Yeh location dekhne ke liye login karein."); return; }
    fetchLocationShareDetail(id).then(setS).catch((e) => {
      setS(null);
      setErr(!e?.response ? "Network issue — location load nahi ho payi. Internet check karke dobara try karein." : e.response.status === 404 ? GONE : formatApiError(e));
    });
  }, [id, user?.id, authChecking]); // eslint-disable-line react-hooks/exhaustive-deps

  const gone = () => { setS(null); setErr(GONE); };
  const go = async () => { setOpening(true); await goTo(s.id, gone); setOpening(false); };
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="location-share-detail">
      <Link to="/notifications" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-emerald-700 mb-4" data-testid="location-share-back"><ArrowLeft className="w-4 h-4" /> Notifications</Link>
      <div className="rounded-2xl border border-slate-100 bg-white p-4 sm:p-5 min-w-0">
        <div className="font-display font-bold text-slate-900 flex items-center gap-2 mb-2"><MapPinned className="w-5 h-5 text-emerald-700" /> Shared location</div>
        {s === undefined && !err && <Loader2 className="w-5 h-5 animate-spin text-slate-400" data-testid="location-share-loading" />}
        {s && <Countdown iso={s.expires_at} onEnd={gone} />}
        {err && <p className="text-sm text-rose-700" data-testid="location-share-error">{err}</p>}
        {s && s.direction === "customer_to_provider" && (
          <ReceivedRow s={s} onGone={gone} onReply={(r) => setS((x) => ({ ...x, my_reply: r }))} />
        )}
        {s && s.direction === "provider_to_customer" && (
          <div data-testid="location-share-from-provider">
            <div className="text-sm font-semibold text-slate-900">{s.provider_name || "Provider"} ne aapke saath location share ki hai</div>
            <div className="text-xs text-slate-500">{s.service_name ? `${s.service_name} · ` : ""}{s.label ? `${s.label} · ` : ""}{fmt(s.expires_at)} tak (IST)</div>
            <button type="button" onClick={go} disabled={opening} className={`${primary} mt-3`} data-testid="location-share-directions">{opening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Navigation className="w-3.5 h-3.5" />} Get Directions to Provider</button>
          </div>
        )}
      </div>
    </div>
  );
}
